import { format, formatDistanceToNow, intervalToDuration } from "date-fns";

/**
 * Standard date-time format used across the application: "yyyy-MM-dd HH:mm"
 * Example: "2025-01-29 14:30"
 */
export const STANDARD_DATETIME_FORMAT = "yyyy-MM-dd HH:mm";

/**
 * Standard date-time format with seconds: "yyyy-MM-dd HH:mm:ss"
 * Example: "2025-01-29 14:30:45"
 */
export const STANDARD_DATETIME_SECONDS_FORMAT = "yyyy-MM-dd HH:mm:ss";

/**
 * Standard date-time format with milliseconds: "yyyy-MM-dd HH:mm:ss.SSS"
 * Example: "2025-01-29 14:30:45.123"
 */
export const STANDARD_DATETIME_MILLISECONDS_FORMAT = "yyyy-MM-dd HH:mm:ss.SSS";

/**
 * Standard date format: "yyyy-MM-dd"
 * Example: "2025-01-29"
 */
export const STANDARD_DATE_FORMAT = "yyyy-MM-dd";

/**
 * User-friendly date format: "dd MMM yyyy"
 * Example: "29 Jan 2025"
 */
export const FRIENDLY_DATE_FORMAT = "dd MMM yyyy";

/**
 * Standard time format (24-hour): "HH:mm:ss"
 * Example: "14:30:45"
 */
export const STANDARD_TIME_FORMAT = "HH:mm:ss";

/**
 * Short time format (24-hour, no seconds): "HH:mm"
 * Example: "14:30"
 */
export const SHORT_TIME_FORMAT = "HH:mm";

/**
 * Internal helper function to safely format a date
 * @param dateInput - Date object, ISO string, or timestamp
 * @param formatString - date-fns format string
 * @param fallback - Fallback string to return if invalid (default: "N/A")
 * @returns Formatted date string or fallback if invalid
 */
function safeFormat(
	dateInput: Date | string | number | null | undefined,
	formatString: string,
	fallback: string = "N/A"
): string {
	if (!dateInput) {
		return fallback;
	}

	try {
		const date = new Date(dateInput);
		if (isNaN(date.getTime())) {
			return fallback;
		}
		return format(date, formatString);
	} catch {
		return fallback;
	}
}

/**
 * Formats a date/timestamp to standard datetime format (yyyy-MM-dd HH:mm)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted date string or "N/A" if invalid
 */
export function formatDateTime(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, STANDARD_DATETIME_FORMAT);
}

/**
 * Formats a date/timestamp to standard datetime format with seconds (yyyy-MM-dd HH:mm:ss)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted date string or "N/A" if invalid
 */
export function formatDateTimeWithSeconds(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, STANDARD_DATETIME_SECONDS_FORMAT);
}

/**
 * Formats a date/timestamp to standard datetime format with milliseconds (yyyy-MM-dd HH:mm:ss.SSS)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted date string or "N/A" if invalid
 */
export function formatDateTimeWithMilliseconds(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, STANDARD_DATETIME_MILLISECONDS_FORMAT);
}

/**
 * Formats a date/timestamp to standard date format (yyyy-MM-dd)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted date string or "N/A" if invalid
 */
export function formatDate(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, STANDARD_DATE_FORMAT);
}

/**
 * Formats a date/timestamp to user-friendly date format (dd MMM yyyy)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted date string or "N/A" if invalid
 * @example formatFriendlyDate("2025-01-29") // "29 Jan 2025"
 */
export function formatFriendlyDate(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, FRIENDLY_DATE_FORMAT);
}

/**
 * Formats a date/timestamp to time only (HH:mm:ss)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted time string or "N/A" if invalid
 * @example formatTime("2025-01-29T14:30:45") // "14:30:45"
 */
export function formatTime(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, STANDARD_TIME_FORMAT);
}

/**
 * Formats a date/timestamp to short time (HH:mm, no seconds)
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted time string or "N/A" if invalid
 * @example formatShortTime("2025-01-29T14:30:45") // "14:30"
 */
export function formatShortTime(
	dateInput: Date | string | number | null | undefined
): string {
	return safeFormat(dateInput, SHORT_TIME_FORMAT);
}

/**
 * Formats a date/timestamp to short relative time (e.g., "5d", "2h", "3m")
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted short relative time string or "—" if invalid
 * @example formatRelativeTimeShort("2025-01-10") // "5d" (if today is Jan 15)
 */
export function formatRelativeTimeShort(
	dateInput: Date | string | number | null | undefined
): string {
	if (!dateInput) {
		return "—";
	}

	try {
		const date = new Date(dateInput);
		if (isNaN(date.getTime())) {
			return "—";
		}

		const now = new Date();
		const isFuture = date.getTime() > now.getTime();
		const prefix = isFuture ? "in " : "";
		// Calendar-accurate decomposition (handles leap years and variable month
		// lengths) so we never produce nonsense like "1y 12mo".
		const d = intervalToDuration({
			start: isFuture ? now : date,
			end: isFuture ? date : now,
		});
		const years = d.years ?? 0;
		const months = d.months ?? 0;
		const days = d.days ?? 0;
		const hours = d.hours ?? 0;
		const minutes = d.minutes ?? 0;

		if (years > 0) {
			return prefix + (months > 0 ? `${years}y ${months}mo` : `${years}y`);
		}
		if (months > 0) {
			return prefix + (days > 0 ? `${months}mo ${days}d` : `${months}mo`);
		}
		if (days > 0) return `${prefix}${days}d`;
		if (hours > 0) return `${prefix}${hours}h`;
		if (minutes > 0) return `${prefix}${minutes}m`;
		return isFuture ? "in <1m" : "<1m";
	} catch {
		return "—";
	}
}

/**
 * Formats a date/timestamp to relative time (e.g., "5 minutes ago")
 * @param dateInput - Date object, ISO string, or timestamp
 * @param options - Optional formatDistanceToNow options
 * @returns Formatted relative time string or "Unknown" if invalid
 */
export function formatRelativeTime(
	dateInput: Date | string | number | null | undefined,
	options?: { addSuffix?: boolean; includeSeconds?: boolean }
): string {
	if (!dateInput) {
		return "Unknown";
	}

	try {
		const date = new Date(dateInput);
		if (isNaN(date.getTime())) {
			return "Unknown";
		}
		return formatDistanceToNow(date, options);
	} catch {
		return "Unknown";
	}
}

/**
 * Formats a duration in seconds to human-readable format with decimal precision
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "1.5s", "2m 30.2s", "1h 5m 12.3s")
 * @example formatDurationWithPrecision(1.5) // "1.5s"
 * @example formatDurationWithPrecision(150.7) // "2m 30.7s"
 * @example formatDurationWithPrecision(3672.4) // "1h 1m 12.4s"
 */
export function formatDurationWithPrecision(seconds: number): string {
	if (seconds < 1) {
		return `${Math.round(seconds * 1000)}ms`;
	}
	if (seconds < 60) {
		return `${seconds.toFixed(1)}s`;
	}

	const duration = intervalToDuration({
		start: 0,
		end: Math.floor(seconds) * 1000,
	});

	const parts: string[] = [];
	if (duration.days) parts.push(`${duration.days}d`);
	if (duration.hours) parts.push(`${duration.hours}h`);
	if (duration.minutes) parts.push(`${duration.minutes}m`);

	// Add seconds with decimal precision
	const remainingSeconds = seconds % 60;
	if (remainingSeconds > 0 || parts.length === 0) {
		parts.push(`${remainingSeconds.toFixed(1)}s`);
	}

	return parts.join(" ");
}

/**
 * Formats a duration in seconds to human-readable format WITHOUT seconds
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "5m", "2h 30m", "1d 5h")
 * @example formatDurationShort(300) // "5m"
 * @example formatDurationShort(9000) // "2h 30m"
 * @example formatDurationShort(104400) // "1d 5h"
 */
export function formatDurationShort(seconds: number): string {
	const duration = intervalToDuration({
		start: 0,
		end: Math.floor(seconds) * 1000,
	});

	const parts: string[] = [];
	if (duration.days) parts.push(`${duration.days}d`);
	if (duration.hours) parts.push(`${duration.hours}h`);
	if (duration.minutes) parts.push(`${duration.minutes}m`);

	// If less than a minute, show "<1m"
	if (parts.length === 0) {
		return "<1m";
	}

	return parts.join(" ");
}

/**
 * Formats time elapsed since a date with decimal seconds precision
 * @param dateInput - Date object, ISO string, or timestamp
 * @returns Formatted time elapsed string (e.g., "3.2s ago", "4m 15.5s ago")
 * @example formatPreciseTimeAgo("2025-01-29T14:30:45") // "3.2s ago"
 */
export function formatPreciseTimeAgo(
	dateInput: Date | string | number | null | undefined
): string {
	if (!dateInput) {
		return "Unknown";
	}

	try {
		const now = new Date();
		const date = new Date(dateInput);
		if (isNaN(date.getTime())) {
			return "Unknown";
		}
		const secondsElapsed = (now.getTime() - date.getTime()) / 1000;

		return `${formatDurationWithPrecision(secondsElapsed)} ago`;
	} catch {
		return "Unknown";
	}
}
